import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { PORTER_TABLE } from "utils/designTokens";

const { cell, header, row, scrollbar, defaultMaxHeight } = PORTER_TABLE;

const cellStyle = {
  fontFamily: cell.fontFamily,
  fontSize: cell.fontSize,
  fontWeight: cell.fontWeight,
  lineHeight: cell.lineHeight,
  color: cell.color,
  padding: cell.padding,
  verticalAlign: "middle",
  borderBottom: `1px solid ${row.borderColor}`,
  background: "transparent",
};

const headerCellStyle = {
  fontFamily: cell.fontFamily,
  fontSize: header.fontSize,
  fontWeight: header.fontWeight,
  color: header.color,
  textTransform: header.textTransform,
  letterSpacing: header.letterSpacing,
  padding: header.padding,
  background: "transparent",
  borderBottom: `1px solid ${row.borderColor}`,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

function PorterTable({ columns, rows, maxHeight, onRowClick, emptyMessage }) {
  const totalMinWidth = useMemo(
    () =>
      columns.reduce((sum, col) => {
        if (!col.width) return sum + 100;
        const parsed = parseInt(col.width, 10);
        return sum + (isNaN(parsed) ? 100 : parsed);
      }, 0),
    [columns],
  );

  const isFill = maxHeight === "fill";

  const containerStyle = {
    overflow: "auto",
    ...(isFill
      ? { height: "100%", flexGrow: 1 }
      : { maxHeight: maxHeight || defaultMaxHeight }),
  };

  const renderedHead = useMemo(
    () => (
      <thead>
        <tr
          style={{
            background: header.background,
            backdropFilter: header.backdropFilter,
          }}
        >
          {columns.map((col, i) => {
            const content =
              typeof col.header === "function" ? col.header() : col.name;
            return (
              <th
                key={col.key || i}
                style={{
                  ...headerCellStyle,
                  width: col.width || "auto",
                  textAlign: "center",
                }}
              >
                {content}
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [columns],
  );

  const renderedBody = useMemo(() => {
    if (!rows || rows.length === 0) {
      return (
        <tbody>
          <tr>
            <td
              colSpan={columns.length}
              style={{
                ...cellStyle,
                textAlign: "center",
                padding: "40px 16px",
                color: "rgba(255,255,255,0.45)",
                borderBottom: "none",
              }}
            >
              {emptyMessage || "No data available."}
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody>
        {rows.map((rowData, rowIdx) => {
          const isLast = rowIdx === rows.length - 1;
          return (
            <tr
              key={rowIdx}
              onClick={onRowClick ? () => onRowClick(rowData) : undefined}
              className="porter-table-row"
              style={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {columns.map((col, colIdx) => {
                const value = rowData[col.key];
                const content = col.render ? col.render(value, rowData) : value;
                return (
                  <td
                    key={col.key || colIdx}
                    style={{
                      ...cellStyle,
                      textAlign: col.align || "left",
                      ...(col.noWrap ? { whiteSpace: "nowrap" } : {}),
                      ...(isLast ? { borderBottom: "none" } : {}),
                    }}
                  >
                    {content != null ? content : ""}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    );
  }, [rows, columns, onRowClick, emptyMessage]);

  return (
    <div style={containerStyle} className="porter-table-container">
      <style>{`
        .porter-table-container::-webkit-scrollbar {
          width: ${scrollbar.width};
          height: ${scrollbar.height};
        }
        .porter-table-container::-webkit-scrollbar-thumb {
          background-color: ${scrollbar.thumbColor};
          border-radius: ${scrollbar.thumbRadius};
        }
        .porter-table-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .porter-table-row:hover td {
          background: ${row.hoverBg} !important;
        }
      `}</style>
      <table
        style={{
          width: "100%",
          minWidth: `${totalMinWidth}px`,
          borderCollapse: "collapse",
          tableLayout: "auto",
        }}
      >
        {renderedHead}
        {renderedBody}
      </table>
    </div>
  );
}

PorterTable.defaultProps = {
  columns: [],
  rows: [],
  maxHeight: undefined,
  onRowClick: undefined,
  emptyMessage: "No data available.",
};

PorterTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
      header: PropTypes.func,
      render: PropTypes.func,
    }),
  ),
  rows: PropTypes.arrayOf(PropTypes.object),
  maxHeight: PropTypes.string,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export default React.memo(PorterTable);
